import React, { useState } from "react";
import { Modal, Checkbox, CheckboxProps } from "semantic-ui-react";
import Select from 'react-select';
import ProgressLoader from "../../Common/ProgressLoader";
import BasicButton from '../../Common/Button';

interface AffectedOffice {
    office_id: number;
    office_code?: string;
    office_name: string;
    in_progress_app_count?: number;
    in_progress_app_codes?: string[];
}

interface BulkChangeRMModalProps {
    open: boolean;
    loading: boolean;
    allRmUsers: any[];
    handleCancel: () => void;
    handleFindOffices: (oldRmId: number) => Promise<AffectedOffice[] | null>;
    handleApply: (officeIds: number[], oldRmId: number, newRmId: number) => Promise<any[] | null>;
    config?: any;
}

type Step = 'select' | 'review' | 'result';

// Offices are identified to users by their office code (e.g. "OF1451") and/or name —
// never the internal numeric database id, which means nothing to anyone outside the DB.
const officeLabel = (office: any) => {
    if (!office) return '';
    if (office.office_code && office.office_name) return `${office.office_code} — ${office.office_name}`;
    return office.office_code || office.office_name || '';
};

const BulkChangeRMModal = (props: BulkChangeRMModalProps) => {
    const { open, loading, allRmUsers, handleCancel, handleFindOffices, handleApply, config } = props;
    const [step, setStep] = useState<Step>('select');
    const [selectedOldRM, setSelectedOldRM] = useState<any>(null);
    const [selectedNewRM, setSelectedNewRM] = useState<any>(null);
    const [affectedOffices, setAffectedOffices] = useState<AffectedOffice[]>([]);
    const [checkedOfficeIds, setCheckedOfficeIds] = useState<Set<number>>(new Set());
    const [findError, setFindError] = useState('');
    const [results, setResults] = useState<any[]>([]);

    const oldRmId = selectedOldRM ? Number(selectedOldRM.user_id) : null;
    const newRmId = selectedNewRM ? Number(selectedNewRM.user_id) : null;
    const sameRM = !!oldRmId && !!newRmId && oldRmId === newRmId;
    const canFindOffices = !!oldRmId && !!newRmId && !sameRM;
    const checkedCount = checkedOfficeIds.size;

    // Confirming the swap does not touch these apps — the departing RM's partner-role
    // access is revoked (see cleanupRemovedRmPartnerRoles on the backend) while these
    // apps keep referencing them, so they can end up assigned to an RM who no longer has
    // access to work them. Only counts checked (about-to-be-confirmed) offices.
    const checkedOfficesWithApps = affectedOffices.filter(
        (o) => checkedOfficeIds.has(o.office_id) && (o.in_progress_app_count || 0) > 0
    );
    const totalInProgressAppsChecked = checkedOfficesWithApps.reduce((sum, o) => sum + (o.in_progress_app_count || 0), 0);

    const resetState = () => {
        setStep('select');
        setSelectedOldRM(null);
        setSelectedNewRM(null);
        setAffectedOffices([]);
        setCheckedOfficeIds(new Set());
        setFindError('');
        setResults([]);
    };

    const handleClose = () => {
        resetState();
        handleCancel();
    };

    const handleFindOfficesClick = async () => {
        setFindError('');
        if (!oldRmId) return;
        const offices = await handleFindOffices(oldRmId);
        // null means the request failed — the error is already shown via the page-level
        // overlay, so stay here rather than showing the "not assigned to any office"
        // message, which would be misleading (that's a different, legitimate case).
        if (offices === null) return;
        if (offices.length === 0) {
            setFindError('This RM is not currently assigned to any office.');
            return;
        }
        setAffectedOffices(offices);
        setCheckedOfficeIds(new Set(offices.map((o) => o.office_id)));
        setStep('review');
    };

    const toggleOffice = (officeId: number, checked: boolean) => {
        const next = new Set(checkedOfficeIds);
        if (checked) next.add(officeId); else next.delete(officeId);
        setCheckedOfficeIds(next);
    };

    const toggleAll = (checked: boolean) => {
        setCheckedOfficeIds(checked ? new Set(affectedOffices.map((o) => o.office_id)) : new Set());
    };

    const handleConfirmApply = async () => {
        if (!oldRmId || !newRmId) return;
        const response = await handleApply(Array.from(checkedOfficeIds), oldRmId, newRmId);
        // null means the request failed — the error is already shown via the page-level
        // overlay, so stay on this step rather than showing a misleading empty "complete".
        if (response === null) return;
        setResults(response);
        setStep('result');
    };

    return (
        <Modal
            centered
            size='small'
            open={open}
            onClose={handleClose}
            closeOnEscape={false}
            closeOnDimmerClick={false}
        >
            <Modal.Content>
                <div className="close-button">
                    <button className="email-activity-close-button" onClick={handleClose}>X</button>
                </div>
                <ProgressLoader loading={loading} size='small' />

                {step === 'select' && (
                    <div className="user-form">
                        <h3>Reassign RM</h3>
                        <p>For when an RM resigns or changes role: pick the departing RM, pick their replacement, then review which offices are affected before anything changes.</p>
                        <div className="fields">
                            <div className="four wide field"><p>Departing RM :</p></div>
                            <div className="twelve wide field">
                                <Select
                                    isMulti={false}
                                    options={allRmUsers}
                                    value={selectedOldRM}
                                    onChange={(value: any) => setSelectedOldRM(value)}
                                    placeholder='Select the RM who is leaving/changing role'
                                />
                            </div>
                        </div>
                        <div className="fields">
                            <div className="four wide field"><p>Replacement RM :</p></div>
                            <div className="twelve wide field">
                                <Select
                                    isMulti={false}
                                    options={allRmUsers}
                                    value={selectedNewRM}
                                    onChange={(value: any) => setSelectedNewRM(value)}
                                    placeholder='Select the replacement RM'
                                />
                            </div>
                        </div>
                        {sameRM && (
                            <p style={{ color: '#B3392C' }}>The replacement RM must be different from the departing RM.</p>
                        )}
                        {findError && (
                            <p style={{ color: '#B3392C' }}>{findError}</p>
                        )}
                    </div>
                )}

                {step === 'review' && (
                    <div className="user-form">
                        <h3>Review affected offices</h3>
                        <p>Replacing <b>{selectedOldRM?.label}</b> with <b>{selectedNewRM?.label}</b> on the checked offices below. Uncheck any office that should keep {selectedOldRM?.label} as-is.</p>
                        <div style={{ marginBottom: '10px' }}>
                            <Checkbox
                                label={`Select all (${affectedOffices.length})`}
                                checked={checkedCount === affectedOffices.length}
                                indeterminate={checkedCount > 0 && checkedCount < affectedOffices.length}
                                onChange={(e, data: CheckboxProps) => toggleAll(!!data.checked)}
                            />
                        </div>
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Office</th>
                                    <th>Work-in-progress apps with {selectedOldRM?.label}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {affectedOffices.map((office) => {
                                    const hasApps = (office.in_progress_app_count || 0) > 0;
                                    return (
                                        <tr key={office.office_id} style={hasApps && checkedOfficeIds.has(office.office_id) ? { background: '#F7E6E3' } : undefined}>
                                            <td>
                                                <Checkbox
                                                    checked={checkedOfficeIds.has(office.office_id)}
                                                    onChange={(e, data: CheckboxProps) => toggleOffice(office.office_id, !!data.checked)}
                                                />
                                            </td>
                                            <td>{officeLabel(office)}</td>
                                            <td style={hasApps ? { color: '#B3392C', fontWeight: 600 } : undefined}>
                                                {hasApps ? (office.in_progress_app_codes || []).join(', ') : 0}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {totalInProgressAppsChecked > 0 && (
                            <div style={{ background: '#F7E6E3', color: '#B3392C', padding: '10px 14px', borderRadius: '6px', marginTop: '10px', fontSize: '13px' }}>
                                <b>⚠ Data loss risk:</b> {totalInProgressAppsChecked} work-in-progress {totalInProgressAppsChecked > 1 ? 'apps' : 'app'} across {checkedOfficesWithApps.length} checked {checkedOfficesWithApps.length > 1 ? 'offices' : 'office'} still reference <b>{selectedOldRM?.label}</b>. Confirming this reassignment revokes {selectedOldRM?.label}'s partner-role access to these apps <b>without updating the apps themselves</b> — {selectedOldRM?.label} may lose the ability to work on them, and they'll keep showing {selectedOldRM?.label} as the RM until a rep happens to re-save the app. This tool does not fix these apps for you — review and manually update their RM (via the app itself, or the per-app RM update available to Super Admins) before or after confirming.
                            </div>
                        )}
                    </div>
                )}

                {step === 'result' && (
                    <div className="user-form">
                        <h3>Reassignment complete</h3>
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th>Office</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row: any) => {
                                    const office = affectedOffices.find((o) => o.office_id === row.office_id);
                                    return (
                                        <tr key={row.office_id}>
                                            <td>{officeLabel(office)}</td>
                                            <td style={{ color: row.success ? '#2E7D46' : '#B3392C' }}>
                                                {row.success ? 'Updated' : `Failed: ${row.error}`}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                {step === 'select' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="CANCEL"
                            className="save-button button-border"
                            onClick={handleClose}
                        />
                        <button
                            className={canFindOffices ? "save-button button-border" : "not-active save-button button-border"}
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={() => canFindOffices && handleFindOfficesClick()}
                        >FIND AFFECTED OFFICES</button>
                    </>
                )}
                {step === 'review' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="BACK"
                            className="save-button button-border"
                            onClick={() => setStep('select')}
                        />
                        <button
                            className={checkedCount > 0 ? "save-button button-border" : "not-active save-button button-border"}
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={() => checkedCount > 0 && handleConfirmApply()}
                        >CONFIRM &amp; APPLY ({checkedCount})</button>
                    </>
                )}
                {step === 'result' && (
                    <button
                        className="save-button button-border"
                        style={{ background: config?.client_config_fe?.button_color }}
                        onClick={handleClose}
                    >DONE</button>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default BulkChangeRMModal;
